import { withGitpodSize } from "@/assets/icons/utils";
import { Button } from "@/components/flexkit/Button";
import { cn } from "@/components/podkit/lib/cn";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { ProjectFailedToLoad } from "@/components/projects/ProjectErrors";
import { NewSecretModal } from "@/components/secrets/NewSecretModal";
import { SecretsCard } from "@/components/secrets/SecretsCard";
import { useFlagGroupManagementEnabled } from "@/hooks/use-flag";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useProject, useProjectState } from "@/queries/project-queries";
import { useListSecrets, type PlainSecret } from "@/queries/secret-queries";
import type { PlainMessage } from "@bufbuild/protobuf";
import IconPlusSmall from "central-icons/IconPlusSmall";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";
import { useCallback, useMemo, useState, type FC } from "react";
import { useParams } from "react-router-dom";

export const ProjectSecretsPage: FC = () => {
    const { projectId } = useParams();
    const { data: secrets, isPending, error } = useListSecrets(projectId);
    const { error: errorProject } = useProject(projectId);

    const { value: isGroupManagementEnabled } = useFlagGroupManagementEnabled();
    const { hasPermission } = useResourcePermissions({ enabled: isGroupManagementEnabled });
    const { data: projectState } = useProjectState(projectId);

    // When flag is OFF: if you can view the project, you can manage secrets (old behavior)
    // When flag is ON: check org-wide sharing OR group-based permissions
    //   - If shared org-wide: everyone in org can manage secrets (old behavior)
    //   - Otherwise: check specific secret permissions (supports read-only access via PROJECT_USER role)
    const isSharedOrgWide = projectState?.shared === true;

    const hasCreatePerm = hasPermission(ResourceType.PROJECT, projectId || "", "secret:create");
    const hasUpdatePerm = hasPermission(ResourceType.PROJECT, projectId || "", "secret:update");
    const hasDeletePerm = hasPermission(ResourceType.PROJECT, projectId || "", "secret:delete");

    const canCreateSecret = isGroupManagementEnabled ? isSharedOrgWide || hasCreatePerm : true;
    const canUpdateSecret = isGroupManagementEnabled ? isSharedOrgWide || hasUpdatePerm : true;
    const canDeleteSecret = isGroupManagementEnabled ? isSharedOrgWide || hasDeletePerm : true;

    const [showAddSecretModal, setShowAddSecretModal] = useState(false);

    const onNewSecret = useCallback(() => {
        setShowAddSecretModal(true);
    }, [setShowAddSecretModal]);

    const scope = useMemo(() => new SecretScope({ scope: { case: "projectId", value: projectId ?? "" } }), [projectId]);

    const content = useMemo(() => {
        if (error || errorProject || !projectId) {
            return <ProjectFailedToLoad error={error || errorProject} />;
        }

        if (isPending) {
            return <SkeletonBlock ready={!isPending} failed={!!error} className="h-[160px] w-[800px]" />;
        }

        return (
            <ProjectSecrets
                scope={scope}
                secrets={secrets?.secrets ?? []}
                onNewSecret={onNewSecret}
                canCreateSecret={canCreateSecret}
                canUpdateSecret={canUpdateSecret}
                canDeleteSecret={canDeleteSecret}
            />
        );
    }, [error, errorProject, projectId, isPending, scope, secrets?.secrets, onNewSecret, canCreateSecret, canUpdateSecret, canDeleteSecret]);

    return (
        <div data-testid="project-details-secrets-page">
            {content}
            <NewSecretModal
                open={showAddSecretModal && !!projectId}
                enableSecretContainerRegistry={true}
                scope={scope}
                onClose={() => setShowAddSecretModal(false)}
            />
        </div>
    );
};

const ProjectSecrets: FC<{
    scope: PlainMessage<SecretScope>;
    secrets: PlainSecret[];
    onNewSecret: () => void;
    canCreateSecret: boolean;
    canUpdateSecret: boolean;
    canDeleteSecret: boolean;
}> = ({ scope, secrets, onNewSecret, canCreateSecret, canUpdateSecret, canDeleteSecret }) => {
    return (
        <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
                <Heading2 className="text-base font-bold text-content-primary">Secrets</Heading2>
                <Button
                    size="sm"
                    LeadingIcon={withGitpodSize(IconPlusSmall)}
                    onClick={onNewSecret}
                    className={cn({ hidden: !canCreateSecret })}
                    data-testid="add-secret-modal-trigger-project"
                    data-tracking-id="open-new-secret-modal-secrets-page"
                >
                    New Secret
                </Button>
            </div>

            <div className="w-full">
                <SecretsCard
                    scope={scope}
                    secrets={secrets}
                    onNewSecret={onNewSecret}
                    canCreateSecret={canCreateSecret}
                    canUpdateSecret={canUpdateSecret}
                    canDeleteSecret={canDeleteSecret}
                />
            </div>
        </div>
    );
};
